import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Popover,
  useMediaQuery,
  useTheme,
  alpha,
  Autocomplete,
  Chip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  getCategories,
  createCategory,
  updateCategory,
  deleteCategory,
} from "../../../Services/Services";
import InfiniteScroll from "react-infinite-scroll-component";

const DivisionManagementTable = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [filter, setFilter] = useState("");
  const [nameError, setNameError] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [categoryToDelete, setcategoryToDelete] = useState(null);
  const [categories, setCategories] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [editId, setEditId] = useState(null);
  const [open, setOpen] = useState(false);
  const [categoryForm, setCategoryForm] = useState({
    name: "",
    created_by: localStorage.getItem("user_id"),
  });

  const [multiCategories, setMultiCategories] = useState([
    { name: "" },
  ]);

  // ── Theme-aware helpers ──
  const tableHeadBg = isDark
    ? alpha(theme.palette.primary.main, 0.15)
    : "#f0f4ff";

  const rowBg = (index) =>
    index % 2 === 0
      ? isDark
        ? alpha(theme.palette.common.white, 0.035)
        : "#fafafa"
      : theme.palette.background.paper;

  // ── Fetch ──
  const fetchCategories = async (
    reset = false,
    pageNo = 1,
    search = filter,
  ) => {
    const data = await getCategories(pageNo, search);
    if (reset) {
      setCategories(data.results);
    } else {
      setCategories((prev) => [...prev, ...data.results]);
    }
    setHasMore(!!data.next);
    setPage(pageNo + 1);
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setPage(1);
      fetchCategories(true, 1, filter);
    }, 500);
    return () => clearTimeout(handler);
  }, [filter]);



  useEffect(() => {
    document.body.style.overflow = isMobile ? "auto" : "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isMobile]);

  const handleSave = async (nameParam) => {
    const createdBy = localStorage.getItem("user_id");
    const finalName = nameParam || categoryForm.name;

    if (!finalName.trim()) {
      setNameError("Category name is required");
      return;
    }

    setNameError("");

    const payload = {
      name: finalName,
    };

    if (editId) {
      const update_res = await updateCategory(editId, payload);
      setCategories(
        categories.map((cat) =>
          cat.id === editId ? { ...cat, ...update_res } : cat,
        ),
      );
      toast.success("Category updated successfully");
    } else {
      const response = await createCategory({
        ...payload,
        created_by: createdBy,
      });
      toast.success("Category added successfully");
      setCategories([response, ...categories]);
    }

    setCategoryForm({ name: "", product_type: null, created_by: createdBy });
    setEditId(null);
    setOpen(false);
  };

  // ✅ Multi save
  const handleSubmitAll = async () => {
    const createdBy = localStorage.getItem("user_id");

    const validCategories = multiCategories.filter(
      (c) => c.name.trim() !== "",
    );

    if (validCategories.length === 0) {
      setNameError("Please enter at least one category name");
      return;
    }

    const payload = validCategories.map((c) => ({
      name: c.name.trim(),
      created_by: createdBy,
    }));

    const response = await createCategory(payload);
    if (Array.isArray(response)) {
      setCategories([...response, ...categories]);
    } else {
      setCategories([response, ...categories]);
    }

    setMultiCategories([{ name: "", product_type: null }]);
    setOpen(false);
    toast.success("Categories added successfully");
  };

  const handleEdit = (cat) => {
    setCategoryForm({
      name: cat.name,
    });
    setEditId(cat.id);
    setOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await deleteCategory(id);
      setCategories((prev) => prev.filter((cat) => cat.id !== id));
      toast.success("Deleted successfully");
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const handleAddField = () => {
    if (multiCategories.length >= 5) {
      toast.error("You can add maximum 5 categories");
      return;
    }
    setMultiCategories([...multiCategories, { name: "" }]);
  };

  const handleRemoveField = (index) => {
    setMultiCategories(multiCategories.filter((_, i) => i !== index));
  };

  const handleChange = (index, field, value) => {
    const newFields = [...multiCategories];
    newFields[index][field] = value;
    setMultiCategories(newFields);
  };

  function formatOnlyDate(dateTimeString) {
    if (!dateTimeString) return "N/A";
    const datePart = dateTimeString.split("T")[0];
    const [year, month, day] = datePart.split("-");
    return `${day}-${month}-${year}`;
  }

  const closeDialog = () => {
    setOpen(false);
    setEditId(null);
 setCategoryForm({ name: "" });
    setMultiCategories([{ name: "" }]);
    setNameError("");
  };

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100%" }}>
      {/* ── Search + Add Bar ── */}
      <Box
        display="flex"
        flexWrap="wrap"
        gap={2}
        alignItems="center"
        sx={{
          mt: { xs: 3, md: 1 },
          mb: 1,
          position: "sticky",
          top: 0,
          zIndex: 1100,
          pt: 1,
          pb: 1,
          px: 0.5,
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: (t) => alpha(t.palette.background.paper, 0.95),
          backdropFilter: "blur(8px)",
          boxShadow: isDark
            ? "0 2px 10px rgba(0,0,0,0.45)"
            : "0 1px 4px rgba(0,0,0,0.07)",
        }}
      >
        {/* Search */}
        <Box sx={{ flexGrow: 1, minWidth: 200 }}>
          <TextField
            label="Search by category"
            variant="outlined"
            size="small"
            fullWidth
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            sx={{
              "& .MuiInputBase-root": {
                height: 30,
                fontSize: "0.7rem",
                borderRadius: 0,
              },
              "& .MuiInputLabel-root": { fontSize: "0.7rem" },
            }}
          />
        </Box>

        {/* Add Button */}
        <Box sx={{ ml: "15px" }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon sx={{ fontSize: 18 }} />}
            sx={{
              minHeight: "30px",
              fontSize: "0.7rem",
              padding: "4px 10px",
              borderRadius: "0px",
            }}
            onClick={() => {
              setEditId(null);
              setCategoryForm({ name: "" });
              setMultiCategories([{ name: "" }]);
              setNameError("");
              setOpen(true);
            }}
          >
            Category
          </Button>
        </Box>
      </Box>

      {/* ── Table / Cards ── */}
      <div id="project-table-print">
        <InfiniteScroll
          dataLength={categories.length}
          next={() => fetchCategories(false, page, filter)}
          hasMore={hasMore}
          loader={
            <Box sx={{ textAlign: "center", py: 2 }}>
              <Typography
                sx={{
                  fontSize: { xs: "0.85rem", sm: "1rem" },
                  color: "text.secondary",
                }}
              >
                Loading...
              </Typography>
            </Box>
          }
          endMessage={
            <Typography
              sx={{
                textAlign: "center",
                py: 2,
                fontSize: { xs: "0.85rem", sm: "1rem" },
                color: "text.disabled",
              }}
            >
              No more categories
            </Typography>
          }
        >
          {isMobile ? (
            /* ── MOBILE CARDS ── */
            <Box sx={{ bgcolor: "background.paper" }}>
              {categories.map((cat, index) => (
                <Box
                  key={cat.id || index}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    p: 1.5,
                    bgcolor: rowBg(index),
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    transition: "background-color 0.15s ease",
                    "&:active": { bgcolor: "action.selected" },
                  }}
                >
                  {/* Left Info */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: "0.85rem",
                        fontWeight: 600,
                        color: "text.primary",
                        mb: 0.3,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <span
                        style={{
                          color: theme.palette.text.disabled,
                          fontSize: "0.75rem",
                        }}
                      >
                        {index + 1}
                      </span>{" "}
                      {cat.name}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.7rem",
                        color: "text.disabled",
                        ml: 1,
                      }}
                    >
                      {formatOnlyDate(cat.created_at)}
                    </Typography>

                  </Box>

                  {/* Action Buttons */}
                  <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
                    {/* Edit */}
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(cat);
                        setOpen(true);
                      }}
                      sx={{
                        bgcolor: (t) =>
                          t.palette.mode === "dark"
                            ? alpha(t.palette.primary.main, 0.15)
                            : "#e3f2fd",
                        borderRadius: "4px",
                        width: 32,
                        height: 32,
                        "&:hover": {
                          bgcolor: (t) =>
                            t.palette.mode === "dark"
                              ? alpha(t.palette.primary.main, 0.28)
                              : "#bbdefb",
                        },
                      }}
                    >
                      <EditIcon
                        sx={{ fontSize: "0.95rem", color: "primary.main" }}
                      />
                    </IconButton>

                    {/* Delete */}
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setAnchorEl(e.currentTarget);
                        setcategoryToDelete(cat.id);
                      }}
                      sx={{
                        bgcolor: (t) =>
                          t.palette.mode === "dark"
                            ? alpha(t.palette.error.main, 0.15)
                            : "#ffebee",
                        borderRadius: "4px",
                        width: 32,
                        height: 32,
                        "&:hover": {
                          bgcolor: (t) =>
                            t.palette.mode === "dark"
                              ? alpha(t.palette.error.main, 0.28)
                              : "#ffcdd2",
                        },
                      }}
                    >
                      <DeleteIcon
                        sx={{ fontSize: "0.95rem", color: "error.main" }}
                      />
                    </IconButton>
                  </Box>
                </Box>
              ))}
            </Box>
          ) : (
            /* ── DESKTOP TABLE ── */
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                maxHeight: "88vh",
                overflowX: "auto",
                overflowY: "auto",
                width: "100%",
                bgcolor: "background.paper",
                backgroundImage: "none",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 0,
                boxShadow: isDark ? "0 4px 18px rgba(0,0,0,0.35)" : "none",
              }}
            >
              <Table
                stickyHeader
                size="small"
                sx={{
                  minWidth: 1000,
                  "& td, & th": {
                    textAlign: "center",
                    whiteSpace: "nowrap",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                  },
                }}
              >
                <TableHead
                  sx={{
                    "& th": {
                      fontWeight: "bold",
                      color: "text.primary",
                      borderBottom: "2px solid",
                      borderColor: "primary.main",
                      backgroundColor: tableHeadBg,
                      fontSize: "0.75rem",
                      letterSpacing: "0.04em",
                    },
                  }}
                >
                  <TableRow>
                    <TableCell
                      sx={{
                        position: "sticky",
                        left: 0,
                        zIndex: 3,
                        backgroundColor: tableHeadBg,
                        minWidth: 50,
                      }}
                    >
                      S.No.
                    </TableCell>
                    <TableCell sx={{ backgroundColor: tableHeadBg }}>
                      Name
                    </TableCell>
                   
                    <TableCell sx={{ backgroundColor: tableHeadBg }}>
                      Date
                    </TableCell>
                    <TableCell
                      className="no-print"
                      sx={{
                        position: "sticky",
                        right: 0,
                        backgroundColor: tableHeadBg,
                        zIndex: 3,
                        minWidth: 80,
                        padding: 0,
                      }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {categories.map((cat, index) => (
                    <TableRow
                      hover
                      key={cat.id || index}
                      sx={{
                        backgroundColor: rowBg(index),
                        transition: "background-color 0.15s ease",
                        "&:hover": {
                          backgroundColor: (t) =>
                            t.palette.mode === "dark"
                              ? `${alpha(
                                  t.palette.primary.main,
                                  0.1,
                                )} !important`
                              : "#e3f2fd !important",
                          "& td": {
                            backgroundColor: "inherit !important",
                          },
                        },
                        "& td": {
                          paddingRight: "13px !important",
                          paddingLeft: "13px !important",
                          padding: "0px",
                          fontSize: { xs: "0.65rem", sm: "0.75rem" },
                          whiteSpace: "nowrap",
                          color: "text.primary",
                        },
                      }}
                    >
                      <TableCell
                        sx={{
                          position: "sticky",
                          left: 0,
                          bgcolor: "inherit",
                          zIndex: 1,
                        }}
                      >
                        {index + 1}
                      </TableCell>
                      <TableCell>{cat.name}</TableCell>
                      <TableCell>{formatOnlyDate(cat.created_at)}</TableCell>
                      <TableCell
                        className="no-print"
                        sx={{
                          position: "sticky",
                          right: 0,
                          bgcolor: "inherit",
                          zIndex: 1,
                          minWidth: 80,
                        }}
                      >
                        <IconButton
                          color="primary"
                          sx={{
                            padding: 0,
                            paddingLeft: 1,
                            paddingRight: 1,
                          }}
                          size="small"
                          onClick={() => {
                            handleEdit(cat);
                            setOpen(true);
                          }}
                        >
                          <EditIcon transform="scale(0.6)" />
                        </IconButton>
                        <IconButton
                          sx={{
                            padding: 0,
                            paddingLeft: 1,
                            paddingRight: 1,
                          }}
                          color="error"
                          size="small"
                          onClick={(e) => {
                            setAnchorEl(e.currentTarget);
                            setcategoryToDelete(cat.id);
                          }}
                        >
                          <DeleteIcon transform="scale(0.6)" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </InfiniteScroll>
      </div>

      {/* ══════════════════════════════════════════════ */}
      {/* ADD / EDIT DIALOG                             */}
      {/* ══════════════════════════════════════════════ */}
      <Dialog
        open={open}
        onClose={closeDialog}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            bgcolor: "background.paper",
            backgroundImage: "none",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: isDark
              ? "0 12px 40px rgba(0,0,0,0.65)"
              : "0 8px 32px rgba(0,0,0,0.14)",
          },
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: "1px solid",
            borderColor: "divider",
            py: 1.5,
            bgcolor: (t) =>
              t.palette.mode === "dark"
                ? alpha(t.palette.primary.main, 0.15)
                : alpha(t.palette.primary.main, 0.06),
            color: "text.primary",
            fontWeight: 700,
            fontSize: "0.95rem",
          }}
        >
          Manage Categories
        </DialogTitle>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (editId) {
              handleSave();
            } else {
              handleSubmitAll();
            }
          }}
        >
          <DialogContent dividers sx={{ bgcolor: "background.paper" }}>
            {editId ? (
              // ── Edit Mode ──
              <Box
                sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 2 }}
              >
                {/* Category Name */}
                <TextField
                  label="Category Name"
                  fullWidth
                  value={categoryForm.name}
                  onChange={(e) => {
                    setCategoryForm((prev) => ({
                      ...prev,
                      name: e.target.value,
                    }));
                    if (e.target.value.trim()) setNameError("");
                  }}
                  error={!!nameError}
                  helperText={nameError}
                  required
                  sx={{
                    "& .MuiInputBase-root": {
                      borderRadius: 0,
                      fontSize: "0.8rem",
                    },
                  }}
                />
              </Box>
            ) : (
              // ── Add Mode (Multiple) ──
              <>
                {multiCategories.map((cat, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      gap: 1.5,
                      mb: 2,
                      alignItems: "flex-start",
                    }}
                  >
                    {/* Category Name */}
                    <TextField
                      label={`Category ${index + 1}`}
                      fullWidth
                      value={cat.name}
                      onChange={(e) =>
                        handleChange(index, "name", e.target.value)
                      }
                      required
                      size="small"
                      sx={{
                        "& .MuiInputBase-root": {
                          borderRadius: 0,
                          fontSize: "0.8rem",
                        },
                      }}
                    />

                    {/* Remove Row Button */}
                    <Box sx={{ width: 36, flexShrink: 0, pt: 0.5 }}>
                      {multiCategories.length > 1 && (
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => handleRemoveField(index)}
                          sx={{
                            "&:hover": {
                              bgcolor: (t) =>
                                t.palette.mode === "dark"
                                  ? alpha(t.palette.error.main, 0.15)
                                  : "#ffebee",
                            },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                ))}

                {nameError && (
                  <Typography color="error" variant="caption">
                    {nameError}
                  </Typography>
                )}
              </>
            )}
          </DialogContent>

          {/* ADD ROW row */}
          <DialogActions
            sx={{
              justifyContent: "flex-start",
              mb: -5,
              bgcolor: "background.paper",
            }}
          >
            <Button
              startIcon={<AddIcon sx={{ fontSize: 18 }} />}
              onClick={handleAddField}
              sx={{
                height: 35,
                fontSize: 14,
                padding: "2px 10px",
                minWidth: "auto",
                textTransform: "none",
                border: "none",
                display: "flex",
                bgcolor: "transparent",
                color: "primary.main",
                "&:hover": {
                  bgcolor: (t) =>
                    t.palette.mode === "dark"
                      ? alpha(t.palette.primary.main, 0.1)
                      : alpha(t.palette.primary.main, 0.06),
                },
              }}
            >
              ADD ROW
            </Button>
          </DialogActions>

          {/* Footer Actions */}
          <DialogActions
            sx={{
              borderTop: "1px solid",
              borderColor: "divider",
              px: 2,
              py: 1.2,
              bgcolor: "background.paper",
            }}
          >
            {/* Close */}
            <Button
              color="error"
              variant="outlined"
              size="small"
              onClick={closeDialog}
            >
              CLOSE
            </Button>

            {/* Save (Add mode) */}
            {!editId && (
              <Button
                type="submit"
                variant="contained"
                size="small"
                sx={{
                  borderRadius: 1,
                  textTransform: "none",
                  fontSize: "0.85rem",
                  padding: "4px 12px",
                }}
              >
                SAVE
              </Button>
            )}

            {/* Update (Edit mode) */}
            {editId && (
              <Button type="submit" variant="contained" size="small">
                Update
              </Button>
            )}
          </DialogActions>
        </form>
      </Dialog>

      {/* ── Delete Popover ── */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(null);
          setcategoryToDelete(null);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        PaperProps={{
          sx: {
            bgcolor: "background.paper",
            backgroundImage: "none",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: isDark
              ? "0 8px 28px rgba(0,0,0,0.6)"
              : "0 4px 16px rgba(0,0,0,0.14)",
          },
        }}
      >
        <Box sx={{ p: 2, minWidth: 180 }}>
          <Typography variant="body2" sx={{ color: "text.primary", mb: 1 }}>
            Are you sure you want to delete?
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
              mt: 1,
            }}
          >
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setAnchorEl(null);
                setcategoryToDelete(null);
              }}
            >
              No
            </Button>
            <Button
              color="error"
              variant="contained"
              size="small"
              onClick={async () => {
                try {
                  await handleDelete(categoryToDelete);
                } finally {
                  setAnchorEl(null);
                  setcategoryToDelete(null);
                }
              }}
            >
              Yes
            </Button>
          </Box>
        </Box>
      </Popover>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        theme={isDark ? "dark" : "light"}
      />
    </Box>
  );
};

export default DivisionManagementTable;
